import React from 'react';
import {
    Button, Checkbox, Chip, createStyles, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid,
    Icon, IconButton, makeStyles, Radio, RadioGroup, TextField, Theme, Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import ChipInput from 'material-ui-chip-input';
import { AxiosError, AxiosResponse } from 'axios';

import { ClientModel } from '../../../../Models/ClientModel';
import { UserModel } from '../../../../Models/UserModel';
import { ptBR } from 'date-fns/locale';
import DateFnsUtils from '@date-io/date-fns';
import { DateTypeClassificationFilter } from '../../enum/DateTypeClassificationFilter';
import { FilterClassificationModel, filterClassificationModelInitial } from '../../../../Models/FilterClassificationModel';
import { validNumberList } from '../../../../shared/operators';
import { RevisionContext } from '../RevisionContext';
import ClientService from '../../../../Services/ClientService';
import UserService from '../../../../Services/UserService';
import { AuthContext } from '../../../Authentication/AuthContext';
import { SearchStatus } from '../../../../shared/enum/SearchStatus';
import { HomeContext } from '../../../Home/HomeContext';
import { PageLocation } from '../../../../shared/enum/PageLocation';
import { IInformationToCorrect, informationToCorrectInitialArray } from '../enum/InformationToCorrect';
import { VehicleModel } from '../../../../Models/VehicleModel';
import { ContentModel } from '../../../../Models/ContentModel';
import VehicleService from '../../../../Services/VehicleService';
import ContentClientService from '../../../../Services/ContentClientService';
import { KeywordModel } from '../../../../Models/KeywordModel';
import KeywordService from '../../../../Services/KeywordService';
import OrganogramService from '../../../../Services/OrganogramService';
import { OrganogramDto } from '../../../../Models/OrganogramDto';
import { Organogram } from '../../../../shared/components/Organogram';
import { OrganogramsContext } from '../../../../shared/context/OrganogramsContext';

interface IFilterFieldsProps {
    callbackSearch: () => void,
    callSearch: boolean,
    setCallSearch: React.Dispatch<React.SetStateAction<boolean>>
}

type HandleChangeType = boolean | number | number[] | string | MaterialUiPickersDate | null;

const useStyles = makeStyles((theme: Theme) => createStyles({
    scheduleIcon: {
        padding: 0
    },
    removedNewsGrid: {
        marginTop: theme.spacing(-2)
    }
}));

export function ClassificationFilterFields({ callbackSearch, callSearch, setCallSearch }: IFilterFieldsProps) {
    const classes = useStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);
    const revisionContext = React.useContext(RevisionContext);

    const [startDateFocused, setStartDateFocused] = React.useState<boolean>(false);
    const [endDateFocused, setEndDateFocused] = React.useState<boolean>(false);
    const [selectedClient, setSelectedClient] = React.useState<ClientModel | null>(null);
    const [selectedResponsible, setSelectedResponsible] = React.useState<UserModel | null>(null);
    const [selectedVehicles, setSelectedVehicles] = React.useState<VehicleModel[]>([]);
    const [selectedContents, setSelectedContents] = React.useState<ContentModel[]>([]);
    const [selectedKeywords, setSelectedKeywords] = React.useState<KeywordModel[]>([]);
    const [selectedStartDate, setSelectedStartDate] = React.useState<Date | null>(null);
    const [clientList, setClientList] = React.useState<ClientModel[]>([]);
    const [responsibleList, setResponsibleList] = React.useState<UserModel[]>([]);
    const [vehicleList, setVehicleList] = React.useState<VehicleModel[]>([]);
    const [contentList, setContentList] = React.useState<ContentModel[]>([]);
    const [keywordList, setKeywordList] = React.useState<KeywordModel[]>([]);
    const [organogramDto, setOrganogramDto] = React.useState<OrganogramDto[]>([]);
    const [pageIsBatchCorrection, setPageIsBatchCorrection] = React.useState<boolean>(false);

    const [startDateError, setStartDateError] = React.useState<boolean>(false);
    const [endDateError, setEndDateError] = React.useState<boolean>(false);
    const [dateTypeNotSelectedError, setDateTypeNotSelectedError] = React.useState<boolean>(false);
    const [informationToCorrectNotSelectedError, setInformationToCorrectNotSelectedError] = React.useState<boolean>(false);
    const [clientNotSelectedError, setClientNotSelectedError] = React.useState<boolean>(false);

    const [clientVehicleLoading, setClientVehicleLoading] = React.useState<boolean>(false);
    const [clientContentLoading, setClientContentLoading] = React.useState<boolean>(false);
    const [clientKeywordLoading, setClientKeywordLoading] = React.useState<boolean>(false);
    const [clientOrganogramLoading, setClientOrganogramLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        ClientService.clientByUserService(authContext.login.user.id)
            .getAll()
            .then((res: AxiosResponse) => {
                setClientList(res.data);
                if (res.data.length === 1)
                    setClientList(res.data[0]);
            })
            .catch((error: AxiosError) => {
                setClientList([]);
                homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar os clientes.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });

        UserService.getUserByQuery('', 0, false)
            .then((res: AxiosResponse) => {
                if (res.data) {
                    setResponsibleList(res.data);
                }
            }).catch((error: AxiosError) => {
                setResponsibleList([]);
                homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar os usuários.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            });
    }, []);

    React.useEffect(() => {
        if (selectedClient && selectedClient.id > 0) {
            setClientVehicleLoading(true);
            setClientContentLoading(true);
            setClientKeywordLoading(true);
            setClientOrganogramLoading(true);

            VehicleService.getVehicleByMediaClient(0, selectedClient.id)
                .then((res: AxiosResponse) => { setVehicleList(res.data); })
                .catch((error: AxiosError) => {
                    homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar os veículos do cliente.' +
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                })
                .finally(() => setClientVehicleLoading(false));

            ContentClientService.contentClientService(selectedClient.id)
                .getAll()
                .then((res: AxiosResponse) => { setContentList(res.data); })
                .catch((error: AxiosError) => {
                    homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar os teores do cliente.' +
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                })
                .finally(() => setClientContentLoading(false));

            KeywordService.keywordService(selectedClient.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    const noneElements = res.data.filter((k: KeywordModel) => k.description.toLowerCase() === 'nenhum');
                    const otherElements = res.data.filter((k: KeywordModel) => k.description.toLowerCase() !== 'nenhum');
                    const sortedKeywords = otherElements.sort((a: KeywordModel, b: KeywordModel) =>
                        (a.description).localeCompare(b.description));

                    setKeywordList([...noneElements, ...sortedKeywords]);
                })
                .catch((error: AxiosError) => {
                    homeContext.showError('Ops.. ocorreu um erro ao carregar as palavras chaves do cliente. <br/>' +
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                })
                .finally(() => setClientKeywordLoading(false));

            OrganogramService.organogramService(selectedClient.id)
                .getAll()
                .then((res: AxiosResponse) => {
                    const organogramsDto: OrganogramDto[] = [{
                        level: 1,
                        parentId: null,
                        selectedOrganogram: null,
                        hasOrganograms: false,
                        organogramList: res.data
                    }];
                    setOrganogramDto(organogramsDto);
                })
                .catch((error: AxiosError) => homeContext.showError('', error))
                .finally(() => setClientOrganogramLoading(false));
        }
        else {
            setVehicleList([]);
            setContentList([]);
            setKeywordList([]);
            setOrganogramDto([]);
        }
    }, [selectedClient]);

    React.useEffect(() => {
        if (!homeContext.openLoadingDialog && (clientVehicleLoading || clientContentLoading || clientKeywordLoading || clientOrganogramLoading)) {
            homeContext.setOpenLoadingDialog(true);
        }
        else if (homeContext.openLoadingDialog && !clientVehicleLoading && !clientContentLoading && !clientKeywordLoading && !clientOrganogramLoading) {
            homeContext.setOpenLoadingDialog(false);
        }
    }, [clientVehicleLoading, clientContentLoading, clientKeywordLoading, clientOrganogramLoading]);

    React.useEffect(() => {
        if (callSearch) {
            handleSearch();
            setCallSearch(false);
        }
    }, [callSearch]);

    React.useEffect(() => {
        if (selectedStartDate == null) {
            const filterForNullStartDate = { ...revisionContext.filterClassification };
            filterForNullStartDate.endDate = null;
            filterForNullStartDate.dateType = DateTypeClassificationFilter.NONE;
            revisionContext.setFilterClassification(filterForNullStartDate);
            setDateTypeNotSelectedError(false);
        }
    }, [selectedStartDate]);

    React.useEffect(() => {
        if (homeContext.pageLocation === PageLocation.BATCH_CORRECTION)
            setPageIsBatchCorrection(true);
    }, [homeContext.pageLocation]);

    const informationToCorrectOptions: IInformationToCorrect[] = informationToCorrectInitialArray;

    const oneFieldFilledIn = () => {
        const clientFieldFilled = selectedClient && selectedClient.id > 0;
        const newsIdFieldFilled = revisionContext.filterClassification.classificationIdList.length > 0;
        const newsMetricIdFieldFilled = revisionContext.filterClassification.classificationMetricIdList.length > 0;
        const responsibleFieldFilled = selectedResponsible && selectedResponsible.id > 0;
        const publicationTitleFieldFilled = revisionContext.filterClassification.publicationTitle.length > 0;
        const startDateFieldFilled = revisionContext.filterClassification.startDate != null;
        const endDateFieldFilled = revisionContext.filterClassification.endDate != null;

        return (newsMetricIdFieldFilled || clientFieldFilled || newsIdFieldFilled || responsibleFieldFilled || publicationTitleFieldFilled ||
            startDateFieldFilled || endDateFieldFilled);
    };

    const handleCleanFilter = () => {
        revisionContext.setFilterClassification(filterClassificationModelInitial);
        revisionContext.setInformationToCorrect(null);
        setDateTypeNotSelectedError(false);
        setSelectedClient(null);
        setSelectedResponsible(null);
        setSelectedStartDate(null);
        setInformationToCorrectNotSelectedError(false);
        setClientNotSelectedError(false);
        setSelectedVehicles([]);
        setSelectedContents([]);
        setSelectedKeywords([]);
    };

    const hasError = () => {
        const dateTypeError = selectedStartDate != null &&
            revisionContext.filterClassification.dateType == DateTypeClassificationFilter.NONE;
        const informationNotSelected = pageIsBatchCorrection && revisionContext.informationToCorrect == null;
        const clientNotSelected = pageIsBatchCorrection &&
            (selectedClient == null || selectedClient.id == 0);

        setDateTypeNotSelectedError(dateTypeError);
        setInformationToCorrectNotSelectedError(informationNotSelected);
        setClientNotSelectedError(clientNotSelected);

        return dateTypeError || startDateError || endDateError || informationNotSelected || clientNotSelected;
    };

    const handleSearch = () => {
        if (!hasError()) {

            const organogramIds: number[] = [];
            organogramDto.forEach(o => {
                if (o.selectedOrganogram)
                    organogramIds.push(o.selectedOrganogram.id);
            });

            revisionContext.filterClassification.organogramId = organogramIds;

            callbackSearch();
        }
    };

    const handleChange = (value: HandleChangeType, prop: keyof FilterClassificationModel) => {
        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, [prop]: value });
    };

    const handleIdList = (list: number[]) => {
        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, classificationIdList: list });
    };

    const handleMetricIdList = (list: number[]) => {
        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, classificationMetricIdList: list });
    };

    const handleRadioClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const eventTarget = event.target as HTMLInputElement;
        if (eventTarget.value == revisionContext.filterClassification.dateType.toString())
            handleChange(DateTypeClassificationFilter.NONE, 'dateType');
        else
            handleChange(+eventTarget.value, 'dateType');
    };

    const handleAddVehicle = (vehicle: VehicleModel) => {
        const vehiclesSelected = [...selectedVehicles];
        vehiclesSelected.push(vehicle);

        setSelectedVehicles(vehiclesSelected);

        const selectedIds = [...revisionContext.filterClassification.vehicleId];
        selectedIds.push(vehicle.id);

        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, vehicleId: selectedIds });
    };

    const handleDeleteVehicle = (vehicle: VehicleModel) => {
        const vehiclesSelected = [...selectedVehicles];
        const index = vehiclesSelected.findIndex(v => v.id = vehicle.id);

        vehiclesSelected.splice(index, 1);

        setSelectedVehicles(vehiclesSelected);

        const selectedIds = [...revisionContext.filterClassification.vehicleId];
        const idIndex = selectedIds.findIndex(i => i == vehicle.id);

        selectedIds.splice(idIndex, 1);

        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, vehicleId: selectedIds });
    };

    const handleAddContent = (content: ContentModel) => {
        const contentsSelected = [...selectedContents];
        contentsSelected.push(content);

        setSelectedContents(contentsSelected);

        const selectedIds = [...revisionContext.filterClassification.contentId];
        selectedIds.push(content.id);

        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, contentId: selectedIds });
    };

    const handleDeleteContent = (content: ContentModel) => {
        const contentsSelected = [...selectedContents];
        const index = contentsSelected.findIndex(c => c.id = content.id);

        contentsSelected.splice(index, 1);
        setSelectedContents(contentsSelected);

        const selectedIds = [...revisionContext.filterClassification.contentId];
        const idIndex = selectedIds.findIndex(i => i == content.id);

        selectedIds.splice(idIndex, 1);

        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, contentId: selectedIds });
    };

    const handleAddKeyword = (keyword: KeywordModel) => {
        const keywordsSelected = [...selectedKeywords];
        keywordsSelected.push(keyword);

        setSelectedKeywords(keywordsSelected);

        const selectedIds = [...revisionContext.filterClassification.keywordId];
        selectedIds.push(keyword.id);

        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, keywordId: selectedIds });
    };

    const handleDeleteKeyword = (keyword: KeywordModel) => {
        const keywordsSelected = [...selectedKeywords];
        const index = keywordsSelected.findIndex(v => v.id = keyword.id);

        keywordsSelected.splice(index, 1);

        setSelectedKeywords(keywordsSelected);

        const selectedIds = [...revisionContext.filterClassification.keywordId];
        const idIndex = selectedIds.findIndex(i => i == keyword.id);

        selectedIds.splice(idIndex, 1);

        revisionContext.setFilterClassification({ ...revisionContext.filterClassification, keywordId: selectedIds });
    };

    const getInformationToCorrect = () => {
        if (pageIsBatchCorrection) {
            return (
                <Grid item xs={12} sm={4}>
                    <Autocomplete
                        id="client"
                        value={revisionContext.informationToCorrect}
                        onChange={(_, value: IInformationToCorrect | null) => {
                            if (value != null)
                                revisionContext.setInformationToCorrect(value);
                        }}
                        options={informationToCorrectOptions}
                        getOptionLabel={(option) => option.description}
                        noOptionsText="Sem resultados"
                        loadingText="Carregando..."
                        renderInput={(params) =>
                            <TextField
                                {...params}
                                label="Informação a ser corrigida"
                                variant="outlined"
                                error={pageIsBatchCorrection && informationToCorrectNotSelectedError}
                                helperText={pageIsBatchCorrection && informationToCorrectNotSelectedError ? 'Campo obrigatório' : ''} />
                        } />
                </Grid>
            );
        }
    };

    const getClient = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="client"
                    value={selectedClient}
                    onChange={(_, value: ClientModel | null) => {
                        setSelectedClient(value);
                        if (value != null) {
                            handleChange(value.id, 'clientId');
                            revisionContext.setClientName(value.name);
                        } else
                            handleChange(null, 'clientId');
                    }}
                    options={clientList}
                    getOptionLabel={(option: ClientModel) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined"
                            error={pageIsBatchCorrection && clientNotSelectedError}
                            helperText={pageIsBatchCorrection && clientNotSelectedError ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const sortIds = (ids: number[]) => {
        ids.sort((a, b) => a - b);
    };

    const parseIdList = (input: string): number[] => {
        return Array.from(
            new Set(
                input
                    .split(/[,;\s]+/)         // vírgula, ponto e vírgula ou espaço
                    .map(s => Number(s.trim()))
                    .filter(n => !isNaN(n) && n > 0) // opcional: valida se é número positivo
            )
        ).sort((a, b) => a - b);
    };

    const getNewsId = () => {
        return (
            <Grid item xs={12} sm={pageIsBatchCorrection ? 4 : 8}>
                <ChipInput
                    id='news-id-list'
                    blurBehavior='add'
                    fullWidth
                    value={revisionContext.filterClassification.classificationIdList}
                    onAdd={(chip: string) => {
                        const newIds = parseIdList(chip);
                        if (newIds.length === 0) return;

                        const combinedIds = Array.from(
                            new Set([...revisionContext.filterClassification.classificationIdList, ...newIds])
                        ).sort((a, b) => a - b);

                        handleIdList(combinedIds);
                    }}
                    chipRenderer={(params, index) => (
                        <Chip
                            {...params}
                            key={index}
                            variant='outlined'
                            label={params.value}
                            onDelete={() => {
                                const updatedIds = revisionContext.filterClassification.classificationIdList.filter(
                                    (_, i) => i !== index
                                );
                                handleIdList(updatedIds);
                            }}
                        />
                    )}
                    label="ID da classificação"
                    variant="outlined"
                />
            </Grid>
        );
    };

    const getNewsMetricId = () => {
        return (
            <Grid item xs={12} sm={pageIsBatchCorrection ? 4 : 8}>
                <ChipInput
                    id='news-metric-id-list'
                    blurBehavior='add'
                    fullWidth
                    value={revisionContext.filterClassification.classificationMetricIdList}
                    onAdd={(chip: string) => {
                        if (validNumberList(chip)) {
                            const currentIdList = [...revisionContext.filterClassification.classificationMetricIdList];
                            const newIdList: number[] = chip.split(';').map(Number);
                            newIdList.forEach(i => !currentIdList.includes(i) && currentIdList.push(i));
                            sortIds(currentIdList);
                            handleMetricIdList(currentIdList);
                        }
                    }}
                    chipRenderer={(params, index) => {
                        return (
                            <Chip
                                {...params}
                                key={index}
                                variant='outlined'
                                label={params.value}
                                onDelete={() => {
                                    const idList = [...revisionContext.filterClassification.classificationMetricIdList];
                                    idList.splice(index, 1);
                                    handleMetricIdList(idList);
                                }} />
                        );
                    }}
                    label="ID Metric"
                    variant="outlined" />
            </Grid>
        );
    };


    const getResponsible = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="responsible"
                    value={selectedResponsible}
                    onChange={(_, value: UserModel | null) => {
                        setSelectedResponsible(value);
                        if (value)
                            handleChange(value?.id, 'responsibleId');
                        else
                            handleChange(null, 'responsibleId');
                    }}
                    options={responsibleList}
                    getOptionLabel={(option: UserModel) => option.username ? option.username : ''}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Responsável"
                            variant="outlined" />
                    } />
            </Grid>
        );
    };

    const getPublicationTitle = () => {
        return (
            <Grid item xs={12} sm={8}>
                <TextField
                    label="Título da publicação"
                    variant="outlined"
                    fullWidth
                    value={revisionContext.filterClassification.publicationTitle}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        handleChange(event.target.value, 'publicationTitle');
                    }}
                    inputProps={{ maxLength: 128 }} />
            </Grid>
        );
    };

    const getVehicle = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id='vehicle-filter'
                    multiple
                    filterSelectedOptions
                    options={vehicleList}
                    getOptionLabel={(option: VehicleModel) => option.nomenclature ?? option.name}
                    value={selectedVehicles}
                    loadingText="Carregando..."
                    noOptionsText={selectedClient && selectedClient.id > 0 ? 'Sem resultados' : 'Selecione um cliente'}
                    onChange={(_, __, reason, detail) => {
                        if (detail && detail.option) {
                            if (reason === 'remove-option')
                                handleDeleteVehicle(detail.option);
                            else if (reason === 'select-option')
                                handleAddVehicle(detail.option);
                        } else if (reason === 'clear') {
                            setSelectedVehicles([]);
                            handleChange([], 'vehicleId');
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.nomenclature ?? option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Veículo"
                            variant="outlined" />
                    )}
                />
            </Grid>
        );
    };

    const getContent = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id='content-filter'
                    multiple
                    filterSelectedOptions
                    options={contentList}
                    getOptionLabel={(option: ContentModel) => option.name}
                    value={selectedContents}
                    noOptionsText={selectedClient && selectedClient.id > 0 ? 'Sem resultados' : 'Selecione um cliente'}
                    loadingText="Carregando..."
                    onChange={(_, __, reason, detail) => {
                        if (detail && detail.option) {
                            if (reason === 'remove-option')
                                handleDeleteContent(detail.option);
                            else if (reason === 'select-option')
                                handleAddContent(detail.option);
                        } else if (reason === 'clear') {
                            setSelectedContents([]);
                            handleChange([], 'contentId');
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Teor"
                            variant="outlined" />
                    )}
                />
            </Grid>
        );
    };

    const getKeywords = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id='keyword-filter'
                    multiple
                    filterSelectedOptions
                    options={keywordList}
                    getOptionLabel={(option: KeywordModel) => option.description}
                    value={selectedKeywords}
                    noOptionsText={selectedClient && selectedClient.id > 0 ? 'Sem resultados' : 'Selecione um cliente'}
                    loadingText="Carregando..."
                    onChange={(_, __, reason, detail) => {
                        if (detail && detail.option) {
                            if (reason === 'remove-option')
                                handleDeleteKeyword(detail.option);
                            else if (reason === 'select-option')
                                handleAddKeyword(detail.option);
                        } else if (reason === 'clear') {
                            setSelectedKeywords([]);
                            handleChange([], 'keywordId');
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.description}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Palavra-chave"
                            variant="outlined" />
                    )}
                />
            </Grid>
        );
    };

    const getOrganogram = () => {
        if (organogramDto && organogramDto.length > 0) {
            return (
                <OrganogramsContext.Provider value={{
                    organogramDto, setOrganogramDto
                }}>
                    <Grid item xs={12}>
                        <Grid container direction="row" spacing={3}>
                            {organogramDto.map((item: OrganogramDto) => {
                                if (item.organogramList && item.organogramList.length > 0) {
                                    return (
                                        <Grid item xs={12} sm={4} key={`grid-organogram-${item.parentId}-${item.selectedOrganogram ? item.selectedOrganogram.id : 0}`}>
                                            <Organogram
                                                key={`organogram-${item.parentId}-${item.selectedOrganogram ? item.selectedOrganogram.id : 0}`}
                                                level={item.level}
                                                error={false}
                                                parentId={item.parentId}
                                                selected={item.selectedOrganogram}
                                                organogramsList={item.organogramList}
                                                disableFieldClear={false} />
                                        </Grid>
                                    );
                                }
                            })}
                        </Grid>
                    </Grid>
                </OrganogramsContext.Provider>
            );
        }
        else {
            return (
                <Grid item xs={12}>
                    <Grid container direction="row" spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                fullWidth
                                loadingText="Carregando..."
                                noOptionsText="Selecione um cliente"
                                id="no-selected-client-organogram"
                                options={[]}
                                renderInput={(params) =>
                                    <TextField {...params}
                                        label="Marcas"
                                        variant="outlined" />
                                } />
                        </Grid>
                    </Grid>
                </Grid>
            );
        }
    };

    const getDate = (
        value: Date | null,
        label: string,
        open: boolean,
        disabled: boolean,
        minDate: Date | null,
        onExit: () => void,
        onChange: (date: MaterialUiPickersDate) => void,
        onIconClick: () => void,
        onError: (error: React.ReactNode) => void
    ) => {
        return (
            <Grid item xs={12} sm={3}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <KeyboardDatePicker
                        id="search-date"
                        autoOk
                        disableFuture
                        allowKeyboardControl
                        variant="inline"
                        inputVariant="outlined"
                        label={label}
                        value={value}
                        minDate={minDate ? minDate : new Date('1900-01-01')}
                        open={open}
                        onBlur={onExit}
                        onClose={onExit}
                        onChange={onChange}
                        disabled={disabled}
                        onError={onError}
                        format="dd/MM/yyyy"
                        invalidDateMessage="Formato de data inválido"
                        minDateMessage="A data não deve exceder a data mínima"
                        maxDateMessage="A data não deve exceder a data máxima"
                        keyboardIcon={
                            <IconButton onClick={onIconClick} className={classes.scheduleIcon}>
                                <Icon>date_range</Icon>
                            </IconButton>
                        } />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getDateType = () => {
        return (
            <Grid item xs={12} sm={6}>
                <FormControl
                    component="fieldset"
                    size='small'
                    disabled={!selectedStartDate}
                    error={dateTypeNotSelectedError}>
                    <FormLabel component="legend">
                        <Typography variant="subtitle2" color="textPrimary">Tipo de data</Typography>
                    </FormLabel>
                    <RadioGroup
                        row
                        aria-label="date-type"
                        name="search-date-type"
                        value={revisionContext.filterClassification.dateType}>
                        <FormControlLabel
                            value={DateTypeClassificationFilter.PUBLICATION}
                            control={
                                <Radio
                                    color='primary'
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleRadioClick(event)}
                                    size='small' />
                            }
                            label={
                                <Typography
                                    variant="body1"
                                    color={selectedStartDate ? 'textPrimary' : 'inherit'}>
                                    Publicação
                                </Typography>
                            } />
                        <FormControlLabel
                            value={DateTypeClassificationFilter.INSERTION}
                            control={
                                <Radio
                                    color='primary'
                                    onClick={(event) => handleRadioClick(event)}
                                    size='small' />
                            }
                            label={
                                <Typography
                                    variant="body1"
                                    color={selectedStartDate ? 'textPrimary' : 'inherit'}>
                                    Inserção
                                </Typography>
                            } />
                        <FormControlLabel
                            value={DateTypeClassificationFilter.MODIFICATION}
                            control={
                                <Radio
                                    color='primary'
                                    onClick={(event) => handleRadioClick(event)}
                                    size='small' />
                            }
                            label={<Typography
                                variant="body1"
                                color={selectedStartDate ? 'textPrimary' : 'inherit'}>
                                Modificação
                            </Typography>
                            } />
                    </RadioGroup>
                    {dateTypeNotSelectedError && <FormHelperText>Selecione um tipo de data</FormHelperText>}
                </FormControl>
            </Grid>
        );
    };

    const getRemovedNews = () => {
        return (
            <Grid item xs={12} className={classes.removedNewsGrid}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={revisionContext.filterClassification.removedNews}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event.target.checked, 'removedNews')}
                            name="Exibir notícias removidas"
                            color="primary" />
                    }
                    label="Exibir notícias removidas" />
            </Grid>
        );
    };

    const getButtons = () => {
        return (
            <Grid item xs={12}>
                <Grid
                    container
                    spacing={1}
                    direction="row"
                    justify="flex-end"
                    alignItems="flex-end">
                    <Grid item>
                        <Button
                            size='large'
                            disabled={revisionContext.searchStatus == SearchStatus.LOADING ||
                                (!pageIsBatchCorrection && !oneFieldFilledIn())}
                            onClick={handleCleanFilter}>
                            LIMPAR FILTROS
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant='contained'
                            color='primary'
                            size='large'
                            disabled={revisionContext.searchStatus == SearchStatus.LOADING ||
                                (!pageIsBatchCorrection && !oneFieldFilledIn())}
                            onClick={handleSearch}>
                            {pageIsBatchCorrection ? 'BUSCAR' : 'BUSCAR CLASSIFICAÇÕES'}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {getInformationToCorrect()}
            {getClient()}
            {getNewsId()}
            {getResponsible()}
            {getNewsMetricId()}
            {getVehicle()}
            {getPublicationTitle()}
            {getContent()}
            {getKeywords()}
            {getOrganogram()}
            {getDate(
                selectedStartDate,
                'Início',
                startDateFocused,
                false,
                null,
                () => setStartDateFocused(false),
                (date) => { setSelectedStartDate(date); handleChange(date, 'startDate'); },
                () => setStartDateFocused(true),
                (error) => setStartDateError((error || '').toString().length > 0))}
            {getDate(
                revisionContext.filterClassification.endDate,
                'Fim',
                endDateFocused,
                !selectedStartDate,
                selectedStartDate,
                () => setEndDateFocused(false),
                (date) => handleChange(date, 'endDate'),
                () => setEndDateFocused(true),
                (error) => setEndDateError((error || '').toString().length > 0))}
            {getDateType()}
            {getRemovedNews()}
            {getButtons()}
        </>
    );
}